import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import CountryForm from '../components/countries/CountryForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import { Plus, Pencil, Trash2, CheckSquare, LayoutGrid, List, Globe } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

interface Country {
  id: number;
  name: string;
  code: string;
  alpha2Code?: string;
  flag?: string;
  isActive: boolean;
  createdAt: string;
}

/**
 * Countries overview page with list and card view
 */
const Countries: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to decode Unicode escape sequences
  const decodeUnicodeFlag = (flag: string | undefined): string => {
    if (!flag) return '';
    try {
      // Handle Unicode escape sequences like \uD83C\uDDE9\uD83C\uDDEA
      return flag.replace(/\\u([0-9A-Fa-f]{4})/g, (_, code) => {
        return String.fromCharCode(parseInt(code, 16));
      });
    } catch (error) {
      return flag;
    }
  };

  // Form and dialog states
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState<Country | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk operations
  const [selectedCountries, setSelectedCountries] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await sportsApi.getCountries();
      setCountries(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Länder');
      console.error('Error loading countries:', err);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations
  const handleCreateCountry = () => {
    setEditingCountry(null);
    setShowForm(true);
  };

  const handleEditCountry = (country: Country) => {
    setEditingCountry(country);
    setShowForm(true);
  };

  const handleSaveCountry = async (data: {
    name: string;
    code: string;
    alpha2Code?: string;
    flag?: string;
    isActive?: boolean;
  }) => {
    try {
      setFormLoading(true);

      if (editingCountry) {
        await sportsApi.updateCountry(editingCountry.id, data);
      } else {
        await sportsApi.createCountry(data);
      }

      await loadCountries();
      setShowForm(false);
      setEditingCountry(null);

      showSuccess(editingCountry ? 'Land erfolgreich aktualisiert!' : 'Land erfolgreich erstellt!');
    } catch (err) {
      console.error('Error saving country:', err);
      throw err; // Re-throw to let the form handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCountry = async (country: Country) => {
    try {
      // Check if country can be deleted
      const { canDelete } = await sportsApi.canDeleteCountry(country.id);

      if (!canDelete) {
        showError('Das Land kann nicht gelöscht werden, da es von Ligen oder Vereinen referenziert wird.');
        return;
      }

      setCountryToDelete(country);
      setShowDeleteDialog(true);
    } catch (err) {
      showError('Fehler beim Prüfen der Löschberechtigung');
      console.error('Error checking delete permission:', err);
    }
  };

  const confirmDeleteCountry = async () => {
    if (!countryToDelete) return;

    try {
      setDeleteLoading(true);
      await sportsApi.deleteCountry(countryToDelete.id);
      await loadCountries();
      setShowDeleteDialog(false);
      setCountryToDelete(null);

      showSuccess('Land erfolgreich gelöscht!');
    } catch (err) {
      showError('Fehler beim Löschen des Landes');
      console.error('Error deleting country:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Bulk operations
  const handleSelectCountry = (countryId: number, selected: boolean) => {
    const newSelected = new Set(selectedCountries);
    if (selected) {
      newSelected.add(countryId);
    } else {
      newSelected.delete(countryId);
    }
    setSelectedCountries(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedCountries(new Set(filteredCountries.map(c => c.id)));
    } else {
      setSelectedCountries(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCountries.size === 0) return;

    // For now, just show a warning - bulk delete would need additional backend support
    showWarning(`Bulk-Löschung von ${selectedCountries.size} Ländern würde hier implementiert werden.`);
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (country.alpha2Code && country.alpha2Code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Länder werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={loadCountries}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateCountry} title="Neues Land erstellen">
            <Plus size={14} /> Neu
          </button>
          <button
            className={`page-btn ${bulkMode ? 'page-btn-info' : 'page-btn-secondary'}`}
            onClick={() => setBulkMode(!bulkMode)}
            title="Massenbearbeitung"
          >
            <CheckSquare size={14} /> Auswahl
          </button>
          {bulkMode && selectedCountries.size > 0 && (
            <button className="page-btn page-btn-danger" onClick={handleBulkDelete} title={`${selectedCountries.size} ausgewählte Länder löschen`}>
              <Trash2 size={14} /> Löschen ({selectedCountries.size})
            </button>
          )}
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Länder durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredCountries.length} / {countries.length} Länder</span>
        </div>
      </div>

      {/* Content */}
      {filteredCountries.length === 0 ? (
        <div className="page-empty">
          <h3>Keine Länder gefunden</h3>
          <p>Versuchen Sie einen anderen Suchbegriff.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredCountries.map((country) => (
            <div key={country.id} className="page-card">
              {bulkMode && (
                <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem', zIndex: 1 }}>
                  <input type="checkbox" checked={selectedCountries.has(country.id)} onChange={(e) => handleSelectCountry(country.id, e.target.checked)} />
                </div>
              )}
              <div className="page-card-header">
                <h3 className="page-card-title">
                  {country.flag && <span style={{ fontSize: '1.5rem' }}>{decodeUnicodeFlag(country.flag)}</span>}
                  {country.name}
                </h3>
                <span className={`page-badge ${country.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                  {country.isActive ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <div className="page-card-content">
                <div className="page-card-row"><strong>Code:</strong> {country.code}</div>
                {country.alpha2Code && <div className="page-card-row"><strong>Alpha-2:</strong> {country.alpha2Code}</div>}
                <div className="page-card-row"><strong>Erstellt:</strong> {new Date(country.createdAt).toLocaleDateString('de-DE')}</div>
              </div>
              {!bulkMode && (
                <div className="page-card-actions">
                  <button className="page-btn-icon edit" onClick={() => handleEditCountry(country)} title="Land bearbeiten"><Pencil size={14} /></button>
                  <button className="page-btn-icon delete" onClick={() => handleDeleteCountry(country)} title="Land löschen"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                {bulkMode && <th><input type="checkbox" checked={selectedCountries.size === filteredCountries.length && filteredCountries.length > 0} onChange={(e) => handleSelectAll(e.target.checked)} /></th>}
                <th>Land</th>
                <th>Code</th>
                <th>Alpha-2</th>
                <th>Status</th>
                <th>Erstellt</th>
                {!bulkMode && <th>Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCountries.map((country) => (
                <tr key={country.id}>
                  {bulkMode && <td><input type="checkbox" checked={selectedCountries.has(country.id)} onChange={(e) => handleSelectCountry(country.id, e.target.checked)} /></td>}
                  <td>{country.flag && <span style={{ fontSize: '1.5rem' }}>{decodeUnicodeFlag(country.flag)}</span>} {country.name}</td>
                  <td>{country.code}</td>
                  <td>{country.alpha2Code || '-'}</td>
                  <td><span className={`page-badge ${country.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>{country.isActive ? 'Aktiv' : 'Inaktiv'}</span></td>
                  <td>{new Date(country.createdAt).toLocaleDateString('de-DE')}</td>
                  {!bulkMode && (
                    <td>
                      <div className="page-table-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditCountry(country)} title="Land bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteCountry(country)} title="Land löschen"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Country Form Dialog */}
      {showForm && (
        <CountryForm
          country={editingCountry}
          onSave={handleSaveCountry}
          onCancel={() => {
            setShowForm(false);
            setEditingCountry(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Land löschen"
        message={`Möchten Sie das Land "${countryToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={confirmDeleteCountry}
        onCancel={() => {
          setShowDeleteDialog(false);
          setCountryToDelete(null);
        }}
        loading={deleteLoading}
        type="danger"
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default Countries;
